import { z } from "zod";
import { DEFAULT_PUBLIC_QUOTE_TEMPLATE } from "../quotes";
import { SettingsLockedBySchema } from "../erpIntegrations/settingsLocks";
import { CustomFieldsSchemaSchema } from "./customFields";
import {
  ItemOverrideFieldSelectionSchema,
  QuotePriceRoundingSchema,
  QuoteTemplateIdSchema,
} from "./quotes";

const SettingLimitSchema = z.number().int().positive().nullable();

/** Quote settings in the advanced settings section; an ERP can lock most of them. */
export const QuoteAdvancedSettingsSchema = z.object({
  requireLocation: z.boolean(),
  requireShipTo: z.boolean(),
  requireContact: z.boolean(),
  requireLengthAndWidth: z.boolean(),
  requireLineItem: z.boolean(),
  maxQuantityBreaksPerLine: SettingLimitSchema,
  allowFractionalQuantities: z.boolean(),
  maxQuoteNumberLength: SettingLimitSchema,
  allowDiscountLines: z.boolean(),
  allowDuplicateItems: z.boolean(),
});
export type QuoteAdvancedSettings = z.infer<typeof QuoteAdvancedSettingsSchema>;

/** Partial save of the advanced quote settings; omitted settings keep their stored value. */
export const QuoteAdvancedSettingsUpdateSchema =
  QuoteAdvancedSettingsSchema.partial();
export type QuoteAdvancedSettingsUpdate = z.infer<
  typeof QuoteAdvancedSettingsUpdateSchema
>;

/** Advanced quote settings of an organization that has not changed them: nothing required or restricted. */
export const DEFAULT_QUOTE_ADVANCED_SETTINGS: QuoteAdvancedSettings = {
  requireLocation: false,
  requireShipTo: false,
  requireContact: false,
  requireLengthAndWidth: false,
  requireLineItem: false,
  maxQuantityBreaksPerLine: null,
  allowFractionalQuantities: true,
  maxQuoteNumberLength: null,
  allowDiscountLines: true,
  allowDuplicateItems: true,
};

/** Organization-level quote settings, with ERP locks already applied. */
export const QuoteSettingsSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  termsAndConditions: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  deliveryTerms: z.string().nullable(),
  quoteFormattingUserGuidance: z.string().nullable(),
  publicQuoteTemplate: QuoteTemplateIdSchema.catch(
    DEFAULT_PUBLIC_QUOTE_TEMPLATE,
  ),
  quotePriceRounding: QuotePriceRoundingSchema.nullable(),
  quoteFooter: z.string().nullable(),
  useSignatures: z.boolean(),
  showTotals: z.boolean(),
  showTotalWeight: z.boolean(),
  aggregatePriceListBreaks: z.boolean(),
  priceListBreakGroupField: z.string().nullable(),
  showConfidenceScore: z.boolean(),
  enableCoverLetter: z.boolean(),
  showLocationField: z.boolean(),
  allowItemFieldOverrides: z.boolean(),
  itemOverrideFields: ItemOverrideFieldSelectionSchema,
  allowLineUnitOverride: z.boolean(),
  customFieldsSchema: CustomFieldsSchemaSchema.nullable(),
  lineItemCustomFieldsSchema: CustomFieldsSchemaSchema.nullable(),
  quoteNumberPadWidth: z.number().int().nullable(),
  quoteNumberPrefix: z.string().nullable(),
  defaultQuoteDocumentTemplateId: z.string().nullable(),
  allowPerQuoteDocumentEditing: z.boolean(),
  ...QuoteAdvancedSettingsSchema.shape,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type QuoteSettings = z.infer<typeof QuoteSettingsSchema>;

/** GET /quote-settings response: effective settings plus the ERPs locking each one. */
export const QuoteSettingsResponseSchema = z.object({
  settings: QuoteSettingsSchema.nullable(),
  lockedBy: SettingsLockedBySchema,
});
export type QuoteSettingsResponse = z.infer<typeof QuoteSettingsResponseSchema>;
